import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useUserStore } from "@/hooks/useUserStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

const AVATAR_BUCKET = "avatars";
const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
export const AVATAR_ACCEPT = "image/png,image/jpeg,image/webp";
const AVATAR_ALLOWED_MIME = new Set(["image/png", "image/jpeg", "image/webp"]);

function extensionFor(mime: string): string {
  if (mime === "image/png") return "png";
  if (mime === "image/webp") return "webp";
  return "jpg";
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// Only files we own (storage paths) live in the bucket; OAuth-supplied https
// URLs are simply unlinked from the profile.
function isStoredInBucket(avatarUrl: string): boolean {
  return !/^https?:\/\//.test(avatarUrl);
}

async function removeFromBucket(path: string): Promise<void> {
  if (!isStoredInBucket(path)) return;
  const { error } = await supabase.storage.from(AVATAR_BUCKET).remove([path]);
  if (error) throw error;
}

async function uploadToBucket(path: string, blob: Blob, mime: string): Promise<void> {
  const { error } = await supabase.storage.from(AVATAR_BUCKET).upload(path, blob, {
    upsert: true,
    contentType: mime,
    cacheControl: "3600",
  });
  if (error) throw error;
}

/**
 * Owns the profile picture: picking a file, cropping it to a circle, uploading
 * the cropped blob to storage, and removing it again. The picture persists on
 * its own — it is not part of the personal form's submit.
 */
export function useAvatarUpload() {
  const { userId } = useAuth();
  const userStore = useUserStore();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  const [uploading, setUploading] = useState(false);
  // Bumped after a successful upload/remove to defeat the browser cache on the public URL.
  const [cacheBust, setCacheBust] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Crop dialog: object URL + MIME of the picked file, shown for circular fitting
  // before upload. `cropSrc` is revoked whenever the dialog closes.
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropMime, setCropMime] = useState<string>("image/jpeg");

  const failed = (err: unknown, fallback: string) => {
    toast({
      variant: "destructive",
      title: t.settings.personal.updateFailed,
      description: messageOf(err, fallback),
    });
  };

  // Rejects a file the storage bucket would not accept, surfacing why.
  const rejectFile = (file: File): boolean => {
    if (!AVATAR_ALLOWED_MIME.has(file.type)) {
      failed(null, t.settings.personal.profilePictureInvalidType);
      return true;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      failed(null, t.settings.personal.profilePictureTooLarge);
      return true;
    }
    return false;
  };

  const closeCropDialog = () => {
    setCropOpen(false);
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  // Revoke any dangling preview URL on unmount.
  useEffect(() => {
    return () => {
      if (cropSrc) URL.revokeObjectURL(cropSrc);
    };
  }, [cropSrc]);

  // Writes the new avatar path (or null) to the profile and refreshes its readers.
  const persistAvatarUrl = async (uid: string, avatarUrl: string | null) => {
    const saved = await userStore.updateProfile(uid, { avatar_url: avatarUrl });
    if (!saved) {
      throw new Error(userStore.error || "updateProfile failed");
    }
    setCacheBust(Date.now());
    await queryClient.invalidateQueries({ queryKey: ["user-profile", uid] });
  };

  const pickFile = () => {
    fileInputRef.current?.click();
  };

  // Validate the picked file, then open the circular crop tool. The raw file is
  // never uploaded directly — the cropped square Blob is (see cropped()).
  const fileChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file twice re-fires onChange.
    e.target.value = "";
    if (!file || !userId || rejectFile(file)) return;

    // Replace any previous preview URL before opening the dialog.
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setCropMime(file.type);
    setCropOpen(true);
  };

  const uploadBlob = async (blob: Blob, mime: string) => {
    if (!userId) return;
    setUploading(true);
    try {
      const path = `${userId}/avatar.${extensionFor(mime)}`;
      await uploadToBucket(path, blob, mime);
      await persistAvatarUrl(userId, path);
      closeCropDialog();
      toast({ title: t.settings.personal.profilePictureUploaded });
    } catch (err) {
      console.error("avatar upload failed", err);
      failed(err, t.settings.personal.profilePictureUploadFailed);
    } finally {
      setUploading(false);
    }
  };

  const cropped = (blob: Blob) => {
    void uploadBlob(blob, cropMime);
  };

  const remove = async () => {
    const currentPath = userStore.userProfile?.avatar_url;
    if (!userId || !currentPath) return;
    setUploading(true);
    try {
      await removeFromBucket(currentPath);
      await persistAvatarUrl(userId, null);
      toast({ title: t.settings.personal.profilePictureRemoved });
    } catch (err) {
      console.error("avatar remove failed", err);
      failed(err, t.settings.personal.profilePictureUploadFailed);
    } finally {
      setUploading(false);
    }
  };

  return {
    uploading,
    cacheBust,
    fileInputRef,
    cropOpen,
    cropSrc,
    cropMime,
    setCropOpen,
    closeCropDialog,
    pickFile,
    fileChanged,
    cropped,
    remove,
  };
}
